'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { User, HelpCircle, Menu, Crown, MessageCircle, LogOut, Shield, CheckCircle, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getDisplayPictureUrl, getFallbackAvatarUrl } from '@/utils/imageUtils';
import MobileNavigation from './MobileNavigation';
import { IMAGE_NAMES, getImageUrl } from '@/config/imageConstants';

interface HeaderProps {
  variant?: 'transparent' | 'fixed';
}

export default function Header({ variant = 'transparent' }: HeaderProps) {
  const { user, isAuthenticated, logout, premium, isLoading } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      // The logout function in AuthContext will handle redirecting to login page
    } catch (error) {
      // Logout error occurred
      // Even if logout fails, redirect to login page
      window.location.href = '/login';
    }
    // Note: No finally block needed since page will reload via window.location.href
  };

  useEffect(() => {
    if (variant === 'transparent') {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [variant]);

  // Close mobile menu when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setIsMobileMenuOpen(false);
      setIsUserMenuOpen(false);
    }
  }, [isAuthenticated, isLoading]);

  const openMobileMenu = () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(true);
  };

  const toggleMobileUserMenu = () => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen((prev) => !prev);
  };

  const headerClasses = variant === 'transparent' 
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-[0_0_18px_-2px_rgba(0,0,0,0.12)]' 
          : 'bg-transparent'
      }`
    : 'fixed top-0 left-0 right-0 z-50 bg-white shadow-[0_0_18px_-2px_rgba(0,0,0,0.12)]';

  const textClasses = variant === 'transparent' && !isScrolled 
    ? 'text-white' 
    : 'text-gray-800';

  const logoHref = isAuthenticated ? '/dashboard' : '/';

  return (
    <>
      <header className={headerClasses}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ease-in-out ${isScrolled || variant === 'fixed' ? 'h-20' : 'h-[90px]'}`}>
            {/* Logo */}
            <Link href={logoHref} className="flex items-center space-x-3 group">
              <div className="transition-transform duration-300">
                {isScrolled || variant === 'fixed' ? (
                  <Image
                    src={getImageUrl(IMAGE_NAMES.LOGO)}
                    alt="Marathi Vadhu Var Suchak Logo"
                    width={119}
                    height={55}
                    className="h-[55px] w-auto header-logo"
                    priority
                    unoptimized
                  />
                ) : (
                  <Image
                    src={getImageUrl(IMAGE_NAMES.LOGO_WHITE)}
                    alt="Marathi Vadhu Var Suchak Logo"
                    width={162}
                    height={75}
                    className="h-[75px] w-auto header-logo"
                    priority
                    unoptimized
                  />
                )}
              </div>
            </Link>

            {/* Right side links */}
            <div className="flex items-center space-x-6">
              {/* For public pages, show login by default without checking auth */}
              {/* For protected pages, check isAuthenticated */}
              {!isAuthenticated || isLoading ? (
                <>
                  <Link
                    href="/login"
                    className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
                  >
                    <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                      <User size={20} />
                    </div>
                    <span className="font-normal">Login</span>
                  </Link>
                  <Link
                    href="/help-guide"
                    className={`group flex items-center space-x-2 hover:text-primary-400 transition-all duration-300 ${textClasses}`}
                  >
                    <div className="p-1 rounded-lg bg-white/10 group-hover:bg-primary-400/20 transition-colors">
                      <HelpCircle size={20} />
                    </div>
                    <span className="font-normal">Help</span>
                  </Link>
                </>
              ) : (
                <>
                  {/* Desktop user menu - hidden on mobile */}
                  <div className="relative hidden md:block">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="group flex items-center space-x-3 text-gray-800 hover:text-primary-600 transition-all duration-300 bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-xl"
                    >
                      <div className="relative">
                        {user && getDisplayPictureUrl(user.displayPicture) ? (
                          <Image
                            src={getDisplayPictureUrl(user.displayPicture)!}
                            alt={`${user.firstName} ${user.lastName}`}
                            width={40}
                            height={40}
                            className="rounded-lg object-cover"
                            unoptimized
                          />
                        ) : (
                          <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                            <User size={20} className="text-primary-600" />
                          </div>
                        )}
                        {user?.profileVerified === 'verified' && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle size={12} className="text-white" />
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-sm">
                          Hi, {user?.firstName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user?.memberId}
                        </div>
                      </div>
                      <Menu size={16} className="group-hover:rotate-180 transition-transform duration-300" />
                    </button>

                    {/* User dropdown */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl py-2 z-50 border border-gray-100">
                        {/* User info header */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center space-x-3">
                        {user && getDisplayPictureUrl(user.displayPicture) ? (
                          <Image
                            src={getDisplayPictureUrl(user.displayPicture)!}
                            alt={`${user.firstName} ${user.lastName}`}
                            width={40}
                            height={40}
                            className="rounded-lg object-cover"
                            unoptimized
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <User size={20} className="text-primary-600" />
                          </div>
                        )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {user?.firstName} {user?.lastName}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {user?.email}
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user?.accountStatus === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user?.accountStatus === 'active' ? 'Active' : 'Inactive'}
                                </span>
                                {user?.phoneVerified && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Phone Verified
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Menu items */}
                        <Link
                          href="/messages"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        >
                          <MessageCircle size={16} />
                          <span>Messages</span>
                        </Link>
                        <Link
                          href="/account/my-profile"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        >
                          <User size={16} />
                          <span>My Profile</span>
                        </Link>
                        <Link
                          href="/account"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        >
                          <Shield size={16} />
                          <span>My Account</span>
                        </Link>
                        <Link
                          href="/account/settings"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        >
                          <Settings size={16} />
                          <span>Profile Settings</span>
                        </Link>
                        {(!premium?.isPremium && user?.profileCompletion !== 'premium') && (
                          <Link
                            href="/membership"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          >
                            <Crown size={16} />
                            <span>Upgrade to Premium</span>
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <button 
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <LogOut size={16} />
                            <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mobile user avatar with dropdown - only visible on screens under 899px */}
                  <div className="relative md:hidden">
                    <button
                      onClick={toggleMobileUserMenu}
                      className="relative"
                      style={{ display: 'inline-block', verticalAlign: 'top' }}
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="true"
                    >
                      {user && getDisplayPictureUrl(user.displayPicture) ? (
                        <Image
                          src={getDisplayPictureUrl(user.displayPicture)!}
                          alt={`${user.firstName} ${user.lastName}`}
                          width={40}
                          height={40}
                          className="rounded-lg object-cover"
                          unoptimized
                        />
                      ) : (
                        <div className="p-2 bg-primary-100 rounded-lg">
                          <User size={20} className="text-primary-600" />
                        </div>
                      )}
                      {user?.profileVerified === 'verified' && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle size={12} className="text-white" />
                        </div>
                      )}
                    </button>

                    {/* Mobile user dropdown */}
                    {isUserMenuOpen && (
                      <>
                        <button
                          type="button"
                          aria-label="Close account menu"
                          className="fixed inset-0 z-[55] md:hidden"
                          onClick={() => setIsUserMenuOpen(false)}
                        />
                        <div className="absolute right-0 z-[60] mt-3 w-64 rounded-2xl border border-gray-100 bg-white py-2 shadow-2xl">
                        {/* User info header */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center space-x-3">
                            {user && getDisplayPictureUrl(user.displayPicture) ? (
                              <Image
                                src={getDisplayPictureUrl(user.displayPicture)!}
                                alt={`${user.firstName} ${user.lastName}`}
                                width={40}
                                height={40}
                                className="rounded-lg object-cover"
                                unoptimized
                              />
                            ) : (
                              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                <User size={20} className="text-primary-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {user?.firstName} {user?.lastName}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {user?.email}
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  user?.accountStatus === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user?.accountStatus === 'active' ? 'Active' : 'Inactive'}
                                </span>
                                {user?.phoneVerified && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Phone Verified
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Menu items */}
                        <Link
                          href="/messages"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <MessageCircle size={16} />
                          <span>Messages</span>
                        </Link>
                        <Link
                          href="/account/my-profile"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <User size={16} />
                          <span>My Profile</span>
                        </Link>
                        <Link
                          href="/account"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Shield size={16} />
                          <span>My Account</span>
                        </Link>
                        <Link
                          href="/account/settings"
                          className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings size={16} />
                          <span>Profile Settings</span>
                        </Link>
                        {(!premium?.isPremium && user?.profileCompletion !== 'premium') && (
                          <Link
                            href="/membership"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Crown size={16} />
                            <span>Upgrade to Premium</span>
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <button 
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <LogOut size={16} />
                            <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
                          </button>
                        </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Mobile hamburger menu - only visible on screens under 899px */}
                  <button
                    onClick={openMobileMenu}
                    className={`ml-2 rounded-lg transition-colors md:hidden ${
                      variant === 'transparent' && !isScrolled 
                        ? 'hover:bg-white/10' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Menu size={30} className={textClasses} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - Only show for logged-in users */}
      {isAuthenticated && !isLoading && (
        <MobileNavigation 
          isOpen={isMobileMenuOpen} 
          onClose={() => {
            setIsMobileMenuOpen(false);
            setIsUserMenuOpen(false);
          }} 
        />
      )}
    </>
  );
}
